/**
 * DateRangeSheet — Airbnb-style hotel date-range picker.
 *
 * Desktop  (≥ md): Popover with TWO months side-by-side.
 * Mobile   (< md): Bottom Sheet, slides up from below, FOUR months
 *                  scrollable vertically.
 *
 * Behaviour:
 *   • Picker is "blank" when opened: ignores current props until the
 *     user picks a first date (Airbnb-style).
 *   • First click  → check-in   (range start)
 *     Second click → check-out  (range end) AND closes immediately,
 *     calls onChange({ from, to }).
 *   • Closing without picking a SECOND date does NOT trigger onChange
 *     (forces 2 clicks — caller keeps previous state).
 *   • Past dates and dates > 365 days in the future are disabled.
 *   • Locale auto-selected from i18n.language. Falls back to enUS.
 *
 * NOTE: Returns ISO yyyy-MM-dd formatting to caller is the caller's
 * responsibility — this component speaks plain JS Date objects. The
 * existing Sunhotels-bound search flow uses `format(date, 'yyyy-MM-dd')`
 * downstream which is unchanged.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { addDays, isBefore, startOfDay, format } from 'date-fns';
import {
  enUS, nl, de, fr, es, it, pt, pl, sv, da, tr,
} from 'date-fns/locale';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { useTranslation } from 'react-i18next';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const LOCALE_MAP = { en: enUS, nl, de, fr, es, it, pt, pl, sv, da, tr };

const useIsDesktop = () => {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(min-width: 768px)').matches : true
  );
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDesktop;
};

export const DateRangeSheet = ({
  checkIn,
  checkOut,
  onChange,
  trigger,
  numberOfMonthsDesktop = 2,
  numberOfMonthsMobile = 4,
}) => {
  const { i18n, t } = useTranslation();
  const isDesktop = useIsDesktop();
  const [open, setOpen] = useState(false);
  // Internal selection state — only resolves to onChange on the 2nd click.
  const [range, setRange] = useState(undefined);

  const locale = LOCALE_MAP[i18n.language] || enUS;
  const today = useMemo(() => startOfDay(new Date()), []);
  const maxDate = useMemo(() => addDays(today, 365), [today]);

  // Reset internal state when the picker is (re-)opened so the calendar
  // starts blank (Airbnb-style).
  useEffect(() => {
    if (open) setRange(undefined);
  }, [open]);

  const handleSelect = useCallback(
    (next) => {
      // DayPicker passes either { from, to } or undefined.
      if (!next) {
        setRange(undefined);
        return;
      }
      const { from, to } = next;

      // Treat the FIRST click as a brand-new start, even if the user
      // happened to click before/on the previous range. DayPicker normally
      // keeps `from` and resets `to` on the next click, but if user clicks
      // an earlier date than the existing `from`, react-day-picker swaps
      // them — we keep that default (more forgiving).
      if (from && !to) {
        setRange({ from, to: undefined });
        return;
      }

      if (from && to && !isBefore(to, from) && +to !== +from) {
        // Valid range with 2 distinct dates → commit + close.
        setRange({ from, to });
        // small delay so the user briefly sees the highlighted range
        setTimeout(() => {
          onChange?.({ from, to });
          setOpen(false);
        }, 150);
      }
    },
    [onChange]
  );

  const disabled = useCallback(
    (date) => isBefore(date, today) || isBefore(maxDate, date),
    [today, maxDate]
  );

  const dayPickerProps = {
    mode: 'range',
    selected: range,
    onSelect: handleSelect,
    locale,
    disabled,
    weekStartsOn: 1,                // Monday — European norm
    fromMonth: today,
    toMonth: maxDate,
    // Range visual: shadcn-themed (uses CSS vars defined globally).
    modifiersClassNames: {
      range_start: 'bg-primary text-primary-foreground rounded-full',
      range_end: 'bg-primary text-primary-foreground rounded-full',
      range_middle: 'bg-primary/20 text-foreground rounded-none',
      today: 'font-bold underline',
    },
    classNames: {
      months: isDesktop
        ? 'flex flex-row gap-6 px-4 py-2'
        : 'flex flex-col gap-4 px-2 pb-6',
      month: 'space-y-3',
      caption: 'flex justify-center pt-1 relative items-center',
      caption_label: 'text-sm font-semibold',
      nav: 'space-x-1 flex items-center',
      nav_button: 'h-7 w-7 bg-transparent p-0 opacity-60 hover:opacity-100 hover:bg-accent rounded-md',
      nav_button_previous: 'absolute left-1',
      nav_button_next: 'absolute right-1',
      table: 'w-full border-collapse space-y-1',
      head_row: 'flex',
      head_cell: 'text-muted-foreground rounded-md w-9 font-normal text-[0.75rem]',
      row: 'flex w-full mt-1',
      cell: 'h-9 w-9 text-center text-sm p-0 relative focus-within:relative focus-within:z-20',
      day: 'h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-accent rounded-full transition',
      day_selected: '',
      day_today: '',
      day_outside: 'text-muted-foreground/40',
      day_disabled: 'text-muted-foreground/30 line-through',
      day_hidden: 'invisible',
    },
  };

  const triggerWithRef = trigger ?? null;

  // Helper subtitle for users
  const helperLine = range?.from && !range?.to
    ? t('search.selectCheckout', 'Select check-out date')
    : t('search.selectDates', 'Select your dates');

  if (isDesktop) {
    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild data-testid="daterange-trigger-desktop">
          {triggerWithRef}
        </PopoverTrigger>
        <PopoverContent
          className="w-auto p-0 rounded-2xl shadow-2xl border border-border bg-popover text-popover-foreground"
          align="start"
          sideOffset={8}
        >
          <div className="px-5 pt-4 pb-1 border-b border-border/40">
            <p className="text-sm font-semibold">{helperLine}</p>
            {range?.from && (
              <p className="text-xs text-muted-foreground mt-0.5">
                {format(range.from, 'EEE, MMM d', { locale })}
                {range?.to ? ` → ${format(range.to, 'EEE, MMM d', { locale })}` : ''}
              </p>
            )}
          </div>
          <DayPicker
            {...dayPickerProps}
            numberOfMonths={numberOfMonthsDesktop}
            defaultMonth={today}
          />
        </PopoverContent>
      </Popover>
    );
  }

  // Mobile — bottom Sheet
  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild data-testid="daterange-trigger-mobile">
        {triggerWithRef}
      </SheetTrigger>
      <SheetContent
        side="bottom"
        className="h-[90vh] rounded-t-2xl p-0 bg-popover text-popover-foreground"
      >
        <div className="px-5 pt-5 pb-2 border-b border-border/40 sticky top-0 bg-popover z-10">
          <p className="text-base font-semibold">{helperLine}</p>
          {range?.from && (
            <p className="text-sm text-muted-foreground mt-0.5">
              {format(range.from, 'EEE, MMM d', { locale })}
              {range?.to ? ` → ${format(range.to, 'EEE, MMM d', { locale })}` : ''}
            </p>
          )}
        </div>
        <div className="overflow-y-auto h-[calc(90vh-64px)] pb-6">
          <DayPicker
            {...dayPickerProps}
            numberOfMonths={numberOfMonthsMobile}
            defaultMonth={today}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default DateRangeSheet;
